import { Container, CssBaseline } from '@mui/joy'
import { useNavigate } from 'react-router-dom'
import { Outlet } from 'react-router-dom'
import * as React from 'react'
import GlobalStyles from '@mui/joy/GlobalStyles'
import Box from '@mui/joy/Box'
import IconButton from '@mui/joy/IconButton'
import List from '@mui/joy/List'
import ListItem from '@mui/joy/ListItem'
import ListItemButton, { listItemButtonClasses } from '@mui/joy/ListItemButton'
import ListItemContent from '@mui/joy/ListItemContent'
import Typography from '@mui/joy/Typography'
import Sheet from '@mui/joy/Sheet'
import MenuRoundedIcon from '@mui/icons-material/MenuRounded'
import QrCode2Icon from '@mui/icons-material/QrCode2'
import DisplaySettingsIcon from '@mui/icons-material/DisplaySettings'
import { useGetStoreDataQuery } from '../services/branding'
import {useEffect, useState} from "react";

function Panel() {
  const { data: storeData, isLoading: isStoreDataLoading } =
    useGetStoreDataQuery()

  const navigate = useNavigate()

  const [logoUrl, setLogoUrl] = useState('')
  const [storeName, setStoreName] = useState('')

  useEffect(() => {
    if (storeData && !isStoreDataLoading) {
      setLogoUrl(storeData.logoUrl)
      setStoreName(storeData.name)
    }
  }, [isStoreDataLoading, storeData])

  function goTo(route: string) {
    navigate(route)
  }

  function openSidebar() {
    document.body.style.overflow = 'hidden'
    document.documentElement.style.setProperty('--SideNavigation-slideIn', '1')
  }

  function closeSidebar() {
    document.documentElement.style.removeProperty('--SideNavigation-slideIn')
    document.body.style.removeProperty('overflow')
  }

  function toggleSidebar() {
    const slideIn = window
      .getComputedStyle(document.documentElement)
      .getPropertyValue('--SideNavigation-slideIn')
    if (slideIn) {
      closeSidebar()
    } else {
      openSidebar()
    }
  }

  return (
    <>
      <CssBaseline />
      <Box sx={{ display: 'flex', minHeight: '100dvh' }}>
        <Sheet
          className="Sidebar"
          sx={{
            position: { xs: 'fixed', md: 'sticky' },
            transform: {
              xs: 'translateX(calc(100% * (var(--SideNavigation-slideIn, 0) - 1)))',
              md: 'none',
            },
            transition: 'transform 0.4s, width 0.4s',
            zIndex: 10000,
            height: '100dvh',
            width: 'var(--Sidebar-width)',
            top: 0,
            p: 2,
            flexShrink: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            borderRight: '1px solid',
            borderColor: 'divider',
            backgroundColor: '#FBFCFE',
          }}
        >
          <GlobalStyles
            styles={(theme) => ({
              ':root': {
                '--Sidebar-width': '220px',
                [theme.breakpoints.up('lg')]: {
                  '--Sidebar-width': '240px',
                },
              },
            })}
          />
          <Box
            className="Sidebar-overlay"
            sx={{
              position: 'fixed',
              zIndex: 9998,
              top: 0,
              left: 0,
              width: '100vw',
              height: '100vh',
              opacity: 'var(--SideNavigation-slideIn)',
              backgroundColor: 'var(--joy-palette-background-backdrop)',
              transition: 'opacity 0.4s',
              transform: {
                xs: 'translateX(calc(100% * (var(--SideNavigation-slideIn, 0) - 1) + var(--SideNavigation-slideIn, 0) * var(--Sidebar-width, 0px)))',
                lg: 'translateX(-100%)',
              },
            }}
            onClick={() => closeSidebar()}
          />
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', pl: 1 }}>
            <img
              src={logoUrl}
              loading="lazy"
              alt="uploaded preview"
              style={{ width: '32px', height: '32px', objectFit: 'cover' }}
            />
            <Typography level="title-lg">{storeName}</Typography>
          </Box>
          <Box
            sx={{
              minHeight: 0,
              overflow: 'hidden auto',
              flexGrow: 1,
              display: 'flex',
              flexDirection: 'column',
              [`& .${listItemButtonClasses.root}`]: {
                gap: 1.5,
              },
            }}
          >
            <List
              size="sm"
              sx={{
                gap: 1,
                '--List-nestedInsetStart': '30px',
                '--ListItem-radius': (theme) => theme.vars.radius.sm,
              }}
            >
              <ListItem>
                <ListItemButton onClick={() => goTo('/qr')}>
                  <QrCode2Icon />
                  <ListItemContent>
                    <Typography level="title-sm">QR Kód</Typography>
                  </ListItemContent>
                </ListItemButton>
              </ListItem>

              <ListItem>
                <ListItemButton onClick={() => goTo('/settings')}>
                  <DisplaySettingsIcon />
                  <ListItemContent>
                    <Typography level="title-sm">Kinézet</Typography>
                  </ListItemContent>
                </ListItemButton>
              </ListItem>
            </List>
          </Box>
        </Sheet>

        <Box
          sx={{
            flexGrow: 1,
            p: 2,
          }}
        >
          <Container
            sx={{
              pt: {
                xs: 'calc(12px + var(--Header-height))',
                sm: 'calc(12px + var(--Header-height))',
              },
            }}
          >
            <Outlet />
          </Container>
        </Box>
      </Box>

      <Sheet
        sx={{
          display: { xs: 'flex', md: 'none' },
          alignItems: 'center',
          justifyContent: 'space-between',
          position: 'fixed',
          top: 0,
          width: '100vw',
          height: 'var(--Header-height)',
          zIndex: 9998,
          p: 2,
          gap: 1,
          borderBottom: '1px solid',
          borderColor: 'background.level1',
          boxShadow: 'sm',
        }}
      >
        <GlobalStyles
          styles={(theme) => ({
            ':root': {
              '--Header-height': '52px',
              [theme.breakpoints.up('md')]: {
                '--Header-height': '0px',
              },
            },
          })}
        />
        <IconButton
          onClick={() => toggleSidebar()}
          variant="outlined"
          color="neutral"
          size="sm"
        >
          <MenuRoundedIcon />
        </IconButton>
      </Sheet>
    </>
  )
}

export default Panel
